import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';

import { ModalEvent } from '../../types/modal-event';
import {
  AlertModalParams,
  CodeModalParams,
  ConfirmModalParams,
  EditPhotoModalParams,
  FeedbackModalParams,
  FileUploadModalParams,
  UploadTaskModalParams,
} from '../../types/modal-params';
import { ModalService } from '../../services/modal.service';
import { delay } from 'rxjs/operators';

/**
 * Container of app modals.
 * This modal container is placed in AppComponent and has app modals as Children.
 */
@Component({
  selector: 'app-modal-container',
  templateUrl: './modal-container.component.html',
  styleUrls: ['./modal-container.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ModalContainerComponent implements OnDestroy, OnInit {
  @ViewChild('alertModal') alertModal: TemplateRef<any>;
  @ViewChild('confirmModal') confirmModal: TemplateRef<any>;
  @ViewChild('codeModal') codeModal: TemplateRef<any>;
  @ViewChild('editPhotoModal') editPhotoModal: TemplateRef<any>;
  @ViewChild('feedbackModal') feedbackModal: TemplateRef<any>;
  @ViewChild('helpModal') helpModal: TemplateRef<any>;
  @ViewChild('fileUploadModal') fileUploadModal: TemplateRef<any>;
  @ViewChild('uploadTaskModal') uploadTaskModal: TemplateRef<any>;

  // Current modal params
  public params: {
    alert?: AlertModalParams;
    confirm?: ConfirmModalParams;
    code?: CodeModalParams;
    editPhoto?: EditPhotoModalParams;
    feedback?: FeedbackModalParams;
    help?: FeedbackModalParams;
    fileUpload?: FileUploadModalParams;
    uploadTask?: UploadTaskModalParams;
  } = {};

  private subscription: Subscription;

  constructor(
    private appModalService: ModalService,
    private ngModal: NgbModal
  ) {}

  ngOnInit(): void {
    this.subscription = this.appModalService.modalEvent.subscribe(
      this.handleModalEvent.bind(this)
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  /**
   * Cancel button click handler in Confirm modal
   * @param button Number indicating which button is clicked to close the modal
   *    0 - Close button at the top right corner
   *    1 - Cancel button next to Confirm button
   */
  onConfirmCancel(button: number) {
    this.ngModal.dismissAll();
    if (this.params.confirm.onCancel) {
      this.params.confirm.onCancel(button);
    }
  }

  /**
   * Confirm button click handler in Confirm Modal
   */
  onConfirm(): void {
    this.ngModal.dismissAll();
    if (this.params.confirm.onConfirm) {
      this.params.confirm.onConfirm();
    }
  }

  /**
   * Code modal dismiss handler
   */
  onCodeModalDismiss() {
    this.ngModal.dismissAll();
    if (this.params.code.onCancel) {
      this.params.code.onCancel();
    }
  }

  /**
   * Code modal success handler.
   * This event is fired when the update flow succeeds.
   */
  onCodeModalSuccess() {
    this.ngModal.dismissAll();
    if (this.params.code.onSuccess) {
      this.params.code.onSuccess();
    }
  }

  /**
   * Save event handler in EditPhoto modal
   * @param imgData Image data
   */
  onEditPhotoModalSave(imgData: string) {
    this.ngModal.dismissAll();
    this.params.editPhoto.onSave(imgData);
  }

  /**
   * Submit event handler in Feedback modal
   * @param data Feedback data
   */
  onSubmitFeedback(data: any) {
    this.params.feedback.onSave(data);
  }

  /**
   * Submit event handler in FileUploadModal
   */
  onSubmitFileUpload(data: any) {
    this.params.fileUpload.onSave(data);
  }

  onCloseUploadTask() {
    this.closeModal();
    this.params.uploadTask.onClose();
  }

  /**
   * Event handler of general modal close event
   */
  closeModal() {
    this.ngModal.dismissAll();
  }

  /**
   * Handler of modal events.
   * @param ev Modal event
   */
  private handleModalEvent(ev: ModalEvent<any> | null) {
    switch (ev?.type) {
      case 'alert':
        /**
         * Show alert modal
         */
        this.params.alert = ev.params;
        const modalRef = this.ngModal.open(this.alertModal, {
          backdropClass: 'transparent-backdrop',
        });
        modalRef.dismissed.pipe(delay(500)).subscribe(() => {
          this.params.alert = null;
        });

        // After 5 seconds, hide alert modal automatically
        setTimeout(() => {
          if (this.params.alert) {
            modalRef.dismiss();
          }
        }, 5000);
        break;

      case 'confirm':
        /**
         * Show confirm modal
         */
        this.params.confirm = ev.params;
        this.ngModal.open(this.confirmModal);
        break;

      case 'code':
        /**
         * Show code modal.
         * The code modal is used to handle Email/Phone update flow.
         */
        this.params.code = ev.params;
        this.ngModal.open(this.codeModal, {
          modalDialogClass: 'code-modal',
          backdrop: 'static',
          keyboard: false,
        });
        break;

      case 'editPhoto':
        /**
         * Show EditPhoto modal.
         * This modal is used to edit avatar (resize and crop) in Account page.
         */
        this.params.editPhoto = ev.params;
        this.ngModal.open(this.editPhotoModal, {
          backdrop: 'static',
          keyboard: false,
        });
        break;

      case 'feedback':
        /**
         * Show feedback modal.
         * This modal is used to submit feedback to LeeTrans support.
         */
        this.params.feedback = ev.params;
        this.ngModal.open(this.feedbackModal, {
          backdrop: 'static',
        });
        break;

      case 'help':
        /**
         * Show help modal.
         * This modal is used to show help information.
         */
        this.params.help = ev.params;
        this.ngModal.open(this.helpModal, {
          backdrop: 'static',
        });
        break;

      case 'fileUpload':
        /**
         * Show file upload modal.
         * This modal is used to submit required files such as Driver License, Medical Card, etc.
         */
        this.params.fileUpload = ev.params;
        this.ngModal.open(this.fileUploadModal, {
          backdrop: 'static',
        });
        break;

      case 'uploadTask':
        this.params.uploadTask = ev.params;
        this.ngModal.open(this.uploadTaskModal, {
          backdrop: 'static',
        });
        break;

      default:
        /**
         * Hides modal by default
         */
        this.ngModal?.dismissAll();
        break;
    }
  }
}
